import { parseLocalDateInput } from './dateRangeUtils'

/**
 * Date range labels for per-metric goal periods (monthly / quarterly / annual).
 * Each range starts on the reference date (default: today) and runs forward
 * by one month, three months, or one year respectively.
 */

function startOfDay(date) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

function addMonths(date, months) {
  const result = startOfDay(date)
  const day = result.getDate()
  result.setMonth(result.getMonth() + months)
  // If the target month is shorter (e.g. Jan 31 → Feb), land on last day of month.
  if (result.getDate() !== day) {
    result.setDate(0)
  }
  return result
}

function inclusiveEndAfterMonths(start, months) {
  const end = addMonths(start, months)
  end.setDate(end.getDate() - 1)
  return end
}

function oneYearLater(from) {
  return addMonths(from, 12)
}

function periodMonthsForType(period) {
  switch (period) {
    case 'quarterly':
      return 3
    case 'annual':
      return 12
    case 'monthly':
    default:
      return 1
  }
}

/**
 * @param {Date | string} start
 * @param {'monthly' | 'quarterly' | 'annual' | string} period
 * @returns {Date}
 */
export function getGoalMetricPeriodEndDate(start, period) {
  const startDay = startOfDay(start instanceof Date ? start : new Date(start))
  return inclusiveEndAfterMonths(startDay, periodMonthsForType(period))
}

/**
 * @param {Date | string} start
 * @param {'monthly' | 'quarterly' | 'annual' | string} period
 * @param {Date} [referenceDate]
 */
export function isGoalMetricPeriodExpired(start, period, referenceDate = new Date()) {
  if (!start) return false
  const end = getGoalMetricPeriodEndDate(start, period)
  end.setHours(23, 59, 59, 999)
  const now = startOfDay(referenceDate)
  return now.getTime() > end.getTime()
}

export function getGoalMetricPeriodExtendLabel(period) {
  switch (period) {
    case 'quarterly':
      return 'quarter'
    case 'annual':
      return 'year'
    case 'monthly':
    default:
      return 'month'
  }
}

function formatDayMonthYear(date) {
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatDayMonth(date) {
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  })
}

function formatCompactRange(start, end) {
  if (start.getTime() === end.getTime()) {
    return formatDayMonthYear(start)
  }

  const sameMonth =
    start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
  const sameYear = start.getFullYear() === end.getFullYear()

  if (sameMonth) {
    return `${start.getDate()}–${end.getDate()} ${end.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`
  }

  if (sameYear) {
    return `${formatDayMonth(start)}–${formatDayMonthYear(end)}`
  }

  return `${formatDayMonthYear(start)}–${formatDayMonthYear(end)}`
}

/**
 * Period chip label: calendar duration, e.g. "2026", "Q3 2026", "August 2026".
 */
export function formatGoalMetricPeriodRangeFromClient(period, periodValue, periodStartISO) {
  const normalized = period === "yearly" ? "annual" : period
  const pv = String(periodValue ?? "").trim()
  if (pv.toLowerCase() === "all") return "All"
  const start = periodStartISO || periodStartFromStoredValue(normalized, pv)
  return formatGoalPeriodAnchorLabel(normalized, start)
}

/**
 * @param {'monthly' | 'quarterly' | 'annual' | string} period
 * @param {Date} [referenceDate]
 * @returns {string}
 */
export function formatGoalMetricPeriodRangeTag(period, referenceDate = new Date()) {
  const start = startOfDay(referenceDate)

  switch (period) {
    case 'quarterly':
      return formatCompactRange(start, inclusiveEndAfterMonths(start, 3))
    case 'annual': {
      const end = oneYearLater(start)
      return `${formatDayMonthYear(start)}–${end.getFullYear()}`
    }
    case 'monthly':
    default:
      return formatCompactRange(start, inclusiveEndAfterMonths(start, 1))
  }
}

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export function toGoalPeriodDateKey(date = new Date()) {
  const value = startOfDay(date instanceof Date ? date : new Date(date))
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * @param {'monthly' | 'quarterly' | 'annual' | string} period
 * @param {Date} [referenceDate]
 * @returns {{ year: number, month: number, quarter: number }}
 */
export function getDefaultPeriodAnchor(period, referenceDate = new Date()) {
  const ref = startOfDay(referenceDate)
  const year = ref.getFullYear()
  const month = ref.getMonth() + 1
  const quarter = Math.floor(ref.getMonth() / 3) + 1

  if (period === 'annual') {
    return { year, month: 1, quarter: 1 }
  }

  return { year, month, quarter }
}

/**
 * @param {'monthly' | 'quarterly' | 'annual' | string} period
 * @param {{ year: number, month?: number, quarter?: number }} anchor
 * @returns {{ start: string, end: string } | null}
 */
export function getCalendarPeriodBounds(period, anchor) {
  const year = anchor.year
  const month = anchor.month ?? 1
  const quarter = anchor.quarter ?? 1

  switch (period) {
    case 'annual':
      return {
        start: `${year}-01-01`,
        end: `${year}-12-31`,
      }
    case 'monthly': {
      const startDate = new Date(year, month - 1, 1)
      const endDate = new Date(year, month, 0)
      return {
        start: toGoalPeriodDateKey(startDate),
        end: toGoalPeriodDateKey(endDate),
      }
    }
    case 'quarterly': {
      const startMonth = (quarter - 1) * 3
      const startDate = new Date(year, startMonth, 1)
      const endDate = new Date(year, startMonth + 3, 0)
      return {
        start: toGoalPeriodDateKey(startDate),
        end: toGoalPeriodDateKey(endDate),
      }
    }
    default:
      return null
  }
}

/**
 * @param {'monthly' | 'quarterly' | 'annual' | string} period
 * @param {string | null | undefined} periodStart
 * @param {Date} [referenceDate]
 * @returns {{ year: number, month: number, quarter: number }}
 */
export function parsePeriodAnchor(period, periodStart, referenceDate = new Date()) {
  const defaults = getDefaultPeriodAnchor(period, referenceDate)
  if (!periodStart) return defaults

  const start = parseLocalDateInput(periodStart)
  if (!start) return defaults

  return {
    year: start.getFullYear(),
    month: start.getMonth() + 1,
    quarter: Math.floor(start.getMonth() / 3) + 1,
  }
}

/**
 * Calendar label for a saved duration, e.g. "2026", "Q1 2026", "January 2026".
 * @param {'monthly' | 'quarterly' | 'annual' | string} period
 * @param {string | null | undefined} periodStart
 * @returns {string}
 */
export function formatGoalPeriodAnchorLabel(period, periodStart) {
  const normalized = String(period ?? "monthly").trim().toLowerCase()
  const kind =
    normalized === "yearly" || normalized === "annually" || normalized === "year"
      ? "annual"
      : normalized === "quarter" || normalized === "quarterly"
        ? "quarterly"
        : normalized === "annual"
          ? "annual"
          : "monthly"
  const anchor = parsePeriodAnchor(kind, periodStart)

  switch (kind) {
    case "annual":
      return String(anchor.year)
    case "quarterly":
      return `Q${anchor.quarter} ${anchor.year}`
    default:
      return `${MONTH_LABELS[anchor.month - 1]} ${anchor.year}`
  }
}

/**
 * Resolve a YYYY-MM-DD start from stored API periodValue (e.g. "2026", "2026-03", "2026-Q1").
 */
export function periodStartFromStoredValue(period, periodValue) {
  const normalized = period === 'yearly' ? 'annual' : period
  const pv = String(periodValue ?? '').trim()
  if (normalized === 'annual' && /^\d{4}$/.test(pv)) return `${pv}-01-01`
  if (normalized === 'monthly' && /^\d{4}-\d{2}$/.test(pv)) return `${pv}-01`
  if (normalized === 'monthly' && /^\d{4}$/.test(pv)) return `${pv}-01-01`
  if (normalized === 'quarterly' && /^\d{4}-Q([1-4])$/i.test(pv)) {
    const m = /^(\d{4})-Q([1-4])$/i.exec(pv)
    const month = (parseInt(m[2], 10) - 1) * 3 + 1
    return `${m[1]}-${String(month).padStart(2, '0')}-01`
  }
  return toGoalPeriodDateKey()
}
